import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/index.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { EntityProvider } from "@/lib/entity";
import { HelpProvider } from "@/lib/help";

import LoginScreen from "@/pages/Login";
import SignupScreen from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import Properties from "@/pages/Properties";
import PropertyDetail from "@/pages/PropertyDetail";
import Transactions from "@/pages/Transactions";
import ReceiptScanner from "@/pages/ReceiptScanner";
import Strategy from "@/pages/Strategy";
import RentRoll from "@/pages/RentRoll";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import Mileage from "@/pages/Mileage";
import Recurring from "@/pages/Recurring";
import CostSegregation from "@/pages/CostSegregation";
import Maintenance from "@/pages/Maintenance";
import Documents from "@/pages/Documents";
import CapitalImprovements from "@/pages/CapitalImprovements";
import CpaReviewQueue from "@/pages/CpaReviewQueue";
import HelpPanel from "@/components/Help/HelpPanel";
import FeatureTour from "@/components/Help/FeatureTour";
import ErrorBoundary from "@/components/ErrorBoundary";


import SuperAdminApp from "@/pages/SuperAdmin";

function OwnerOnly({ children }) {
  const { can = {} } = useAuth() || {};
  return can?.admin ? children : <Navigate to="/" replace />;
}

function Gate() {
  const { loading, authed } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--muted)]">
        <div className="text-sm font-semibold tracking-wide animate-pulse">Loading Rental Tracker…</div>
      </div>
    );
  }
  if (!authed) {
    return (
      <Routes>
        <Route path="/signup" element={<SignupScreen />} />
        <Route path="/login" element={<LoginScreen />} />
        <Route path="*" element={<LoginScreen />} />
      </Routes>
    );
  }
  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/properties" element={<Properties />} />
        <Route path="/properties/:id" element={<PropertyDetail />} />
        <Route path="/transactions" element={<ErrorBoundary><Transactions /></ErrorBoundary>} />
        <Route path="/scan" element={<ReceiptScanner />} />
        <Route path="/strategy" element={<Strategy />} />
        <Route path="/rent-roll" element={<RentRoll />} />
        <Route path="/maintenance" element={<Maintenance />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/mileage" element={<Mileage />} />
        <Route path="/recurring" element={<Recurring />} />
        <Route path="/cost-seg" element={<CostSegregation />} />
        <Route path="/improvements" element={<CapitalImprovements />} />
        <Route path="/cpa-review" element={<CpaReviewQueue />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/admin" element={<OwnerOnly><Admin /></OwnerOnly>} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      <HelpPanel />
      <FeatureTour />
    </ErrorBoundary>
  );
}


export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <EntityProvider>
            <HelpProvider>
              <ErrorBoundary>
                <Toaster position="top-center" richColors closeButton />
              </ErrorBoundary>
              <ErrorBoundary>
                <Routes>
                  <Route path="/super-admin/*" element={<ErrorBoundary><SuperAdminApp /></ErrorBoundary>} />
                  <Route path="/*" element={<Gate />} />
                </Routes>
              </ErrorBoundary>
            </HelpProvider>
          </EntityProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
